import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Eye, ExternalLink, Heart, Users, Info, ShieldCheck, Megaphone, TrendingUp, DollarSign } from "lucide-react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

// §12.1 Merchant Statistics — clearly separates INTERNAL SIBLINGO detail views from EXTERNAL
// retailer clicks. Attribution SIBLINGO cannot genuinely measure is labeled honestly, never faked.
// Exposes no child identity, measurement, family composition or private search criteria.

function StatCard({ icon: Icon, label, value, hint, testid, tone = "brand", unavailable }) {
  const toneCls = tone === "external" ? "text-gold-text bg-gold/10 border-gold/25" : "text-brand bg-brand/10 border-brand/20";
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-line shadow-soft p-5">
      <div className="flex items-center justify-between">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center border ${toneCls}`}><Icon size={18} /></span>
        {unavailable && <span className="text-[10px] font-bold uppercase tracking-wide text-ink-muted bg-cream border border-line rounded-full px-2 py-0.5">Not tracked yet</span>}
      </div>
      <p className="mt-3 font-heading font-black text-3xl text-ink" data-testid={`${testid}-value`}>{unavailable ? <span className="text-lg text-ink-muted font-bold">Not available</span> : value}</p>
      <p className="text-sm font-semibold text-ink-soft">{label}</p>
      {hint && <p className="text-xs text-ink-muted mt-1 leading-snug">{hint}</p>}
    </div>
  );
}

function PartnerBlock({ p, showName }) {
  return (
    <div className="space-y-4" data-testid={`merchant-partner-${p.id}`}>
      {showName && (
        <h3 className="font-heading font-extrabold text-lg text-ink flex items-center gap-2">
          <Megaphone size={18} className="text-brand" /> {p.name}
          <span className="text-xs font-semibold text-ink-muted">· {p.deals_live} live / {p.deals_total} total</span>
        </h3>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard testid="stat-detail-views" icon={Eye} tone="brand" label="SIBLINGO detail views" value={p.detail_views}
          hint="Times shoppers opened this deal INSIDE SIBLINGO (not a retailer visit)." />
        <StatCard testid="stat-retailer-clicks" icon={ExternalLink} tone="external" label="Retailer clicks" value={p.retailer_clicks}
          hint="Shoppers who continued OUT to the retailer from the deal page." />
        <StatCard testid="stat-saves" icon={Heart} tone="brand" label="Favorites / saves" value={p.saves}
          hint="Shoppers who saved this deal to revisit." />
        <StatCard testid="stat-qualified" icon={Users} tone="brand" label="Qualified-audience impressions"
          value={p.qualified_impressions} unavailable={!p.qualified_impressions_available}
          hint="Counted only when a listing is served as a complete match for a selected audience." />
        <StatCard testid="stat-ctr" icon={TrendingUp} tone="brand" label="Detail → retailer rate"
          value={p.click_through_rate != null ? `${p.click_through_rate}%` : "—"} unavailable={p.click_through_rate == null}
          hint="Share of SIBLINGO detail views that continued to the retailer." />
        <StatCard testid="stat-conversions" icon={ShieldCheck} tone="brand" label="Purchase conversions"
          value={0} unavailable={!p.conversion_signals_available}
          hint="Retailers don't report purchases to SIBLINGO, so this can't be measured." />
      </div>

      {p.earnings && (
        <div className="bg-white rounded-2xl border border-line p-4" data-testid={`merchant-earnings-${p.id}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <h4 className="font-heading font-extrabold text-ink flex items-center gap-2"><DollarSign size={16} className="text-brand" /> Your earnings <span className="text-[11px] font-semibold text-ink-muted">· private to you</span></h4>
            <span className="text-sm font-black text-brand" data-testid={`earnings-pct-${p.id}`}>{p.earnings.commission_pct != null ? `${p.earnings.commission_pct}%` : "—"}</span>
          </div>
          <p className="text-xs text-ink-muted mb-3">{p.earnings.commission_basis}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Affiliate revenue received", "gross_affiliate_revenue"], ["Earned", "earned"], ["Paid", "paid"], ["Owed", "owed"]].map(([lbl, key]) => (
              <div key={key} className="rounded-xl bg-cream border border-line p-3" data-testid={`earnings-${key}-${p.id}`}>
                <p className="text-[11px] font-semibold text-ink-muted">{lbl}</p>
                {p.earnings.revenue_available && p.earnings[key] != null
                  ? <p className="text-lg font-black text-ink">${Number(p.earnings[key]).toFixed(2)}</p>
                  : <p className="text-xs font-bold text-ink-muted mt-1">Not tracked yet</p>}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-ink-muted mt-2">Period: {p.earnings.period}. We never fabricate conversions or revenue — amounts appear only once affiliate revenue is actually received and recorded.</p>
        </div>
      )}

      {p.per_deal && p.per_deal.length > 0 ? (
        <div className="bg-white rounded-2xl border border-line overflow-hidden" data-testid={`merchant-deals-table-${p.id}`}>
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-2.5 bg-cream text-[11px] font-bold uppercase tracking-wider text-ink-muted">
            <span>Deal</span><span className="text-right">Views</span><span className="text-right">Clicks</span><span className="text-right">Saves</span>
          </div>
          {p.per_deal.map((d) => (
            <div key={d.id} data-testid={`merchant-deal-row-${d.id}`} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-3 border-t border-line items-center">
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-ink truncate">{d.title}</span>
                <span className="text-[11px] text-ink-muted capitalize">{d.status}</span>
              </span>
              <span className="text-right text-sm font-bold text-brand">{d.detail_views}</span>
              <span className="text-right text-sm font-bold text-gold-text">{d.retailer_clicks}</span>
              <span className="text-right text-sm font-bold text-ink">{d.saves}</span>
            </div>
          ))}
        </div>
      ) : (
        <div data-testid={`merchant-deals-empty-${p.id}`} className="bg-white rounded-2xl border border-line p-6 text-center">
          <p className="font-heading font-bold text-ink">No deals yet</p>
          <p className="text-sm text-ink-soft mt-1">Once your submitted deals are reviewed and go live, their view, click and save activity will appear here.</p>
        </div>
      )}
    </div>
  );
}

export default function MerchantStats() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && user.role === "admin";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    const url = isAdmin ? "/admin/merchant-stats" : "/merchant/stats";
    api.get(url).then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [isAdmin]);

  const partners = isAdmin ? (data && data.partners) || [] : (data && data.partner ? [data.partner] : []);

  return (
    <div className="max-w-4xl mx-auto space-y-6 fade-up" data-testid="merchant-stats-page">
      <div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink flex items-center gap-2">
          <BarChart3 className="text-brand" /> {isAdmin ? "Partner Statistics" : "Your Deal Statistics"}
        </h1>
        <p className="text-ink-soft mt-1">
          {isAdmin ? "Performance across every affiliate partner." : "How shoppers are engaging with your deals on SIBLINGO."}
        </p>
      </div>

      <div data-testid="merchant-stats-legend" className="flex items-start gap-2 bg-blush border border-brand/15 rounded-2xl p-4 text-sm text-ink-soft">
        <Info size={16} className="text-brand shrink-0 mt-0.5" />
        <span>
          <b className="text-brand">SIBLINGO detail views</b> count opens of a deal <b>inside</b> SIBLINGO.
          <b className="text-gold-text"> Retailer clicks</b> count shoppers who continued <b>out</b> to the store. These are separate.
          We only show numbers we can genuinely measure — purchase and some attribution data isn't reported back to SIBLINGO, so it's marked <i>Not tracked yet</i>. No child details, sizes, family info or search criteria are ever shown here.
        </span>
      </div>

      {err ? (
        <div data-testid="merchant-stats-error" className="bg-white rounded-3xl border border-line p-10 text-center">
          <p className="font-heading font-extrabold text-xl text-ink">Statistics unavailable</p>
          <p className="text-ink-soft mt-1">We couldn't load your statistics right now. Please try again shortly.</p>
        </div>
      ) : data === null ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="bg-white rounded-2xl border border-line h-32 animate-pulse" />)}
        </div>
      ) : isAdmin && data.totals ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="merchant-totals">
            <StatCard testid="total-partners" icon={Megaphone} label="Partners" value={data.totals.partners} />
            <StatCard testid="total-views" icon={Eye} label="Detail views" value={data.totals.detail_views} />
            <StatCard testid="total-clicks" icon={ExternalLink} tone="external" label="Retailer clicks" value={data.totals.retailer_clicks} />
            <StatCard testid="total-saves" icon={Heart} label="Saves" value={data.totals.saves} />
          </div>
          {partners.length === 0 ? (
            <div data-testid="merchant-stats-empty" className="bg-white rounded-3xl border border-line p-10 text-center">
              <p className="font-heading font-extrabold text-xl text-ink">No affiliate partners yet</p>
              <p className="text-ink-soft mt-1">Partner statistics will appear here once partners are onboarded and their deals go live.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {partners.map((p) => <PartnerBlock key={p.id} p={p} showName />)}
            </div>
          )}
        </>
      ) : partners.length > 0 ? (
        <PartnerBlock p={partners[0]} showName={false} />
      ) : (
        <div data-testid="merchant-stats-empty" className="bg-white rounded-3xl border border-line p-10 text-center">
          <p className="font-heading font-extrabold text-xl text-ink">Nothing to show yet</p>
          <p className="text-ink-soft mt-1">Submit a deal and, once it's reviewed and live, its activity will appear here.</p>
          <button onClick={() => navigate("/post-deal")} data-testid="merchant-stats-post-deal" className="mt-5 h-12 px-6 rounded-full bg-brand text-white font-bold hover:bg-brand-hover transition-all">Post a Deal</button>
        </div>
      )}
    </div>
  );
}
